import React, { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/browser';
import { uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { useUserContext } from '../../contexts/UserContext';
import {
    createHelpSupportDoc,
    updateHelpSupportDoc,
} from '../../graphql/resourceLibraryQueries';
import type { HelpSupportDoc } from '../../types/resourceLibraryTypes';
import { CustomModal } from '../CustomModal';
import { Btn, BtnIconOnly, IconClose, TEIQspinner } from '../ui';
import '../../css/ResourceLibraryContent.css';

interface AddEditDocModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    editingDoc: HelpSupportDoc | null;
}

const ACCEPTED_EXTENSIONS = '.mp4,.mov,.pdf,.jpg,.jpeg,.png';
const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
const SLOW_NETWORK_THRESHOLD_MBPS = 5; // Warn if connection speed is below 5 Mbps
const LARGE_FILE_THRESHOLD_BYTES = 100 * 1024 * 1024; // Show network warning for files > 100 MB

type UploadTab = 'file' | 'link';

export const AddEditDocModal: React.FC<AddEditDocModalProps> = ({
    isOpen,
    onClose,
    onSave,
    editingDoc,
}) => {
    const { companyOwner } = useUserContext();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [docType, setDocType] = useState('');
    const [visibility, setVisibility] = useState('');
    const [uploadTab, setUploadTab] = useState<UploadTab>('file');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [contentUrl, setContentUrl] = useState('');
    const [status, setStatus] = useState<'DRAFT' | 'PUBLISHED'>('DRAFT');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const [networkWarning, setNetworkWarning] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Pre-populate when editing
    useEffect(() => {
        if (isOpen && editingDoc) {
            setTitle(editingDoc.title);
            setDescription(editingDoc.description || '');
            setDocType(editingDoc.docType);
            setVisibility(editingDoc.visibility);
            setStatus(editingDoc.status as 'DRAFT' | 'PUBLISHED');
            setContentUrl(editingDoc.contentUrl || '');
            setUploadTab(editingDoc.contentUrl ? 'link' : 'file');
            setSelectedFile(null);
        } else if (isOpen) {
            setTitle('');
            setDescription('');
            setDocType('');
            setVisibility('');
            setStatus('DRAFT');
            setContentUrl('');
            setUploadTab('file');
            setSelectedFile(null);
        }
        setError(null);
        setUploadProgress(null);
        setNetworkWarning(null);
    }, [isOpen, editingDoc]);

    const isValid = title.trim() && docType && visibility;

    const handleFileSelect = (file: File) => {
        if (file.size > MAX_FILE_SIZE) {
            setError('File size exceeds 2GB limit.');
            setNetworkWarning(null);
            return;
        }
        // Warn about slow network for large files
        if (file.size > LARGE_FILE_THRESHOLD_BYTES) {
            const connection = (navigator as any).connection;
            if (connection && connection.downlink && connection.downlink < SLOW_NETWORK_THRESHOLD_MBPS) {
                setNetworkWarning(
                    `Your network speed appears slow (~${connection.downlink} Mbps). ` +
                    `Uploading ${(file.size / (1024 * 1024)).toFixed(0)} MB may take a while. Consider using a stronger connection or a smaller file.`
                );
            } else {
                setNetworkWarning(null);
            }
        } else {
            setNetworkWarning(null);
        }
        setSelectedFile(file);
        setError(null);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFileSelect(file);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFileSelect(file);
    };

    const handleSave = async () => {
        if (!isValid) return;
        setSaving(true);
        setError(null);

        try {
            let filePath: string | null = editingDoc?.contentFilePath || null;
            let finalContentUrl: string | null = uploadTab === 'link' ? (contentUrl || null) : (editingDoc?.contentUrl || null);

            // Upload file to S3 if a new file is selected
            if (uploadTab === 'file' && selectedFile) {
                setUploadProgress('Uploading file...');
                const fileName = `${uuidv4()}-${selectedFile.name}`;
                const s3Path = `public/helpDocs/${fileName}`;

                const uploadTask = uploadData({
                    path: s3Path,
                    data: selectedFile,
                });
                await uploadTask.result;
                filePath = s3Path;
                finalContentUrl = null;
                setUploadProgress(null);
            }

            if (editingDoc) {
                await updateHelpSupportDoc({
                    id: editingDoc.id,
                    title: title.trim(),
                    description: description.trim() || null,
                    docType,
                    visibility,
                    status,
                    contentFilePath: filePath,
                    contentUrl: finalContentUrl,
                });
            } else {
                await createHelpSupportDoc({
                    title: title.trim(),
                    description: description.trim() || null,
                    docType,
                    visibility,
                    status,
                    contentFilePath: filePath,
                    contentUrl: finalContentUrl,
                    createdByAdminId: companyOwner?.id || '',
                });
            }

            onSave();
        } catch (err) {
            console.error('Error saving doc:', err);
            Sentry.captureException(err);
            setError('Failed to save document. Please try again.');
        } finally {
            setSaving(false);
            setUploadProgress(null);
        }
    };

    return (
        <CustomModal
            visible={isOpen}
            onDismiss={onClose}
            size="large"
            header={editingDoc ? 'Edit Document' : 'Add Document'}
            bodyClassName="zrl-dialog-body"
            footer={
                <div className="modal-footer-actions">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} />
                    <Btn
                        variant="primary"
                        buttonText={saving ? 'Saving...' : 'Save Doc'}
                        buttonDisabled={!isValid || saving}
                        buttonOnClick={handleSave}
                    />
                </div>
            }
        >
                    {/* Document Title */}
                    <div className="zrl-form-group">
                        <label className="zrl-form-label">Document Title *</label>
                        <input
                            className="zrl-input"
                            type="text"
                            placeholder="Enter document title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>

                    {/* Description */}
                    <div className="zrl-form-group">
                        <label className="zrl-form-label">Description</label>
                        <textarea
                            className="zrl-textarea"
                            placeholder="Brief description of this document"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>

                    {/* Doc Type */}
                    <div className="zrl-form-group">
                        <label className="zrl-form-label">Doc Type *</label>
                        <select
                            className="zrl-select"
                            value={docType}
                            onChange={(e) => setDocType(e.target.value)}
                        >
                            <option value="">Select type...</option>
                            <option value="PDF">PDF</option>
                            <option value="Video">Video</option>
                        </select>
                    </div>

                    {/* Visibility / Gating */}
                    <div className="zrl-form-group">
                        <label className="zrl-form-label">Visibility / Gating *</label>
                        <select
                            className="zrl-select"
                            value={visibility}
                            onChange={(e) => setVisibility(e.target.value)}
                        >
                            <option value="">Select visibility...</option>
                            <option value="all">All customers</option>
                            <option value="safety">Safety module only</option>
                            <option value="pm">PM module only</option>
                            <option value="tires">Tires module only</option>
                            <option value="cost-tracking">Cost Tracking module only</option>
                        </select>
                    </div>

                    {/* Upload File or Link */}
                    <div className="zrl-form-group">
                        <label className="zrl-form-label">Upload File or Link</label>
                        <div className="zrl-upload-tabs">
                            <Btn
                                variant={uploadTab === 'file' ? 'primary' : 'secondary-outlined'}
                                buttonSize="sm"
                                buttonText="Upload File"
                                buttonOnClick={() => setUploadTab('file')}
                            />
                            <Btn
                                variant={uploadTab === 'link' ? 'primary' : 'secondary-outlined'}
                                buttonSize="sm"
                                buttonText="Link / URL"
                                buttonOnClick={() => setUploadTab('link')}
                            />
                        </div>

                        {uploadTab === 'file' ? (
                            <div
                                className={`zrl-drop-zone ${dragOver ? 'drag-over' : ''}`}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={ACCEPTED_EXTENSIONS}
                                    onChange={handleFileInputChange}
                                    style={{ display: 'none' }}
                                />
                                {selectedFile ? (
                                    <div className="zrl-drop-zone-selected">
                                        <span>📄 {selectedFile.name}</span>
                                        <span className="zrl-drop-zone-size">
                                            ({(selectedFile.size / (1024 * 1024)).toFixed(1)} MB)
                                        </span>
                                        <BtnIconOnly
                                            size="sm"
                                            icon={<IconClose />}
                                            ariaLabel="Remove selected file"
                                            onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                                        />
                                    </div>
                                ) : (
                                    <div className="zrl-drop-zone-empty">
                                        <span className="zrl-drop-zone-icon">📁</span>
                                        <span>Drag &amp; drop a file here, or click to browse</span>
                                        <span className="zrl-drop-zone-hint">
                                            MP4, MOV, PDF, JPG, PNG — up to 2 GB
                                        </span>
                                    </div>
                                )}
                                {editingDoc?.contentFilePath && !selectedFile && (
                                    <div className="zrl-drop-zone-existing">
                                        Current file: {editingDoc.contentFilePath.split('/').pop()}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <input
                                className="zrl-input"
                                type="url"
                                placeholder="https://example.com/document.pdf"
                                value={contentUrl}
                                onChange={(e) => setContentUrl(e.target.value)}
                            />
                        )}

                        {networkWarning && !uploadProgress && (
                            <div className="zrl-network-warning">
                                ⚠️ {networkWarning}
                            </div>
                        )}
                    </div>

                    {/* Initial Status */}
                    <div className="zrl-form-group">
                        <label className="zrl-form-label">Initial Status</label>
                        <div className="zrl-radio-group">
                            <label
                                className={`zrl-radio-row ${status === 'DRAFT' ? 'selected' : ''}`}
                                onClick={() => setStatus('DRAFT')}
                            >
                                <input
                                    type="radio"
                                    name="docStatus"
                                    checked={status === 'DRAFT'}
                                    onChange={() => setStatus('DRAFT')}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div>
                                    <div className="zrl-radio-label">Save as Draft</div>
                                    <div className="zrl-radio-desc">
                                        Visible only to you, not pushed to customers yet
                                    </div>
                                </div>
                            </label>
                            <label
                                className={`zrl-radio-row ${status === 'PUBLISHED' ? 'selected' : ''}`}
                                onClick={() => setStatus('PUBLISHED')}
                            >
                                <input
                                    type="radio"
                                    name="docStatus"
                                    checked={status === 'PUBLISHED'}
                                    onChange={() => setStatus('PUBLISHED')}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div>
                                    <div className="zrl-radio-label">Publish Immediately</div>
                                    <div className="zrl-radio-desc">
                                        Visible to all eligible customers right away
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Upload Progress */}
                    {uploadProgress && (
                        <div className="zrl-info-banner" style={{ marginTop: 12 }} role="status">
                            <TEIQspinner variant="sm" label={uploadProgress} />
                            <span>{uploadProgress}</span>
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="zrl-warning-banner" style={{ marginTop: 12 }}>
                            <span className="zrl-warning-icon">⚠️</span>
                            <span>{error}</span>
                        </div>
                    )}
        </CustomModal>
    );
};
